/**
 * Chips com uma opção exclusiva e um campo "Outro" — o widget que serve os
 * dois steps de alimentação (restrições e preferências).
 *
 * POR QUE CHIPS E NÃO "SIM/NÃO + TEXTO": o desenho pede chips, e chips
 * respondem melhor à pergunta ("o que você não come?") do que um rádio que
 * só abre uma caixa em branco. O PAYLOAD, porém, é o MESMO de antes:
 *
 *   <CHAVE>          → boolean, `false` só quando a opção exclusiva está marcada
 *   <CHAVE>_DETAILS  → o texto que a IA lê, agora montado a partir dos chips
 *
 * Nada de campo novo, nada de lista onde antes havia string. Quem lê o
 * `_DETAILS` (o prompt do plano) continua recebendo texto livre — só que
 * escrito pelo app em vez de digitado à mão.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Campo, Chips } from '../widgets/Widgets.jsx';
import s from './ChipsComOutro.module.scss';

/** O chip que abre o campo livre. Não vai para o texto final com este rótulo. */
export const OUTRO = '__outro__';

export default function ChipsComOutro({
  opcoes,
  exclusivo,
  placeholder,
  chaveBool,
  chaveTexto,
  setCanProceed,
  setStepData,
  setNeedsButton,
}) {
  const [marcados, setMarcados] = useState([]);
  const [texto, setTexto] = useState('');
  const campoRef = useRef(null);

  // Seleção múltipla: o auto-advance do orquestrador não pode disparar no
  // primeiro toque. Esta é a mesma alavanca que o step de saúde já usava.
  useEffect(() => {
    setNeedsButton?.(true);
    return () => setNeedsButton?.(false);
  }, [setNeedsButton]);

  const semNada = marcados.includes(exclusivo);
  const querOutro = marcados.includes(OUTRO);

  /* O texto que vai para a IA: os rótulos marcados, mais o que a pessoa
     escreveu. A opção exclusiva nunca entra — ela SIGNIFICA lista vazia. */
  const detalhes = useMemo(() => {
    if (semNada) return '';
    const rotulos = marcados
      .filter((v) => v !== OUTRO && v !== exclusivo)
      .map((v) => opcoes.find((o) => o.value === v)?.label || v);
    const livre = texto.trim();
    return [...rotulos, ...(livre ? [livre] : [])].join(', ');
  }, [marcados, texto, semNada, opcoes, exclusivo]);

  useEffect(() => {
    /* Marcar "Outro" e não escrever nada não é resposta: sem esta guarda o
       CTA liberava com o campo em branco e o `_DETAILS` ia vazio com o
       booleano em true. */
    const faltaTexto = querOutro && !texto.trim();
    const valido = marcados.length > 0 && !faltaTexto;

    setCanProceed(valido);
    setStepData(
      valido
        ? { [chaveBool]: !semNada, [chaveTexto]: detalhes }
        : null
    );
  }, [
    marcados, texto, querOutro, semNada, detalhes,
    chaveBool, chaveTexto, setCanProceed, setStepData,
  ]);

  const alternar = (valor) => {
    setMarcados((atual) => {
      // A exclusiva limpa tudo; qualquer outra limpa a exclusiva.
      if (valor === exclusivo) return atual.includes(exclusivo) ? [] : [exclusivo];
      const semExclusivo = atual.filter((v) => v !== exclusivo);
      return semExclusivo.includes(valor)
        ? semExclusivo.filter((v) => v !== valor)
        : [...semExclusivo, valor];
    });
    if (valor === OUTRO) {
      // O campo nasce focado: marcar "Outro" e ter de tocar de novo para
      // escrever é um toque a mais sem motivo.
      requestAnimationFrame(() => campoRef.current?.focus());
    }
  };

  return (
    <div className={s.bloco}>
      <Chips
        opcoes={[...opcoes, { value: OUTRO, label: 'Outro' }]}
        valores={marcados}
        onToggle={alternar}
      />
      {querOutro && (
        <Campo
          ref={campoRef}
          valor={texto}
          onChange={setTexto}
          placeholder={placeholder}
          multilinha
        />
      )}
    </div>
  );
}
